import React, { useState, useEffect, useCallback } from 'react';
import { MessageSquare, Lightbulb, Bug, Heart, MoreHorizontal, X, ChevronLeft, ChevronRight } from 'lucide-react';
import { getOrgFeedback } from '../api';
import { useOrg } from '../contexts/OrgContext';
import { groupByDay } from '../utils/groupByDay';

const timeOf = (iso) => (iso ? new Date(iso).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '');
const initialsOf = (name, email) => {
  const src = (name || email || 'U').trim();
  const parts = src.split(/[\s@.]+/).filter(Boolean);
  return ((parts[0]?.[0] || 'U') + (parts[1]?.[0] || '')).toUpperCase();
};

const CATEGORY = {
  idea: { label: 'Idea', icon: Lightbulb, cls: 'bg-amber-500/10 text-amber-400' },
  bug: { label: 'Bug', icon: Bug, cls: 'bg-red-500/10 text-red-400' },
  praise: { label: 'Praise', icon: Heart, cls: 'bg-emerald-500/10 text-emerald-400' },
  other: { label: 'Other', icon: MoreHorizontal, cls: 'bg-fg/10 text-fg-muted' },
};

function Avatar({ name, email }) {
  return (
    <span className="inline-flex items-center justify-center h-11 w-11 rounded-xl bg-orange/10 text-orange text-sm font-display font-bold shrink-0">
      {initialsOf(name, email)}
    </span>
  );
}

function CategoryTag({ category }) {
  const c = CATEGORY[category];
  if (!c) return null;
  const Icon = c.icon;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-xs font-medium ${c.cls}`}>
      <Icon className="w-3 h-3" /> {c.label}
    </span>
  );
}

function SkeletonCard() {
  return (
    <div className="bg-panel border border-panel-border rounded-xl p-4 animate-pulse">
      <div className="flex items-center gap-3">
        <div className="h-11 w-11 rounded-xl bg-field" />
        <div className="flex-1 space-y-2">
          <div className="h-3 w-44 bg-field rounded" />
          <div className="h-2.5 w-64 bg-field rounded" />
        </div>
      </div>
    </div>
  );
}

export default function FeedbackInbox() {
  const { active } = useOrg();
  const orgId = active?.org_id;
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [msg, setMsg] = useState(null);
  const [filterCat, setFilterCat] = useState('');
  const [selected, setSelected] = useState(null);
  const [page, setPage] = useState(1);
  const PER_PAGE = 18;

  const load = useCallback(async () => {
    if (!orgId) { setLoading(false); return; }
    setLoading(true);
    try {
      const { data } = await getOrgFeedback(orgId);
      setItems(data.feedback || []);
    } catch (e) {
      setMsg({ kind: 'err', text: e.response?.data?.detail || 'Failed to load feedback' });
    } finally {
      setLoading(false);
    }
  }, [orgId]);
  useEffect(() => { load(); }, [load]);

  const shown = filterCat ? items.filter((f) => f.category === filterCat) : items;
  const totalPages = Math.max(1, Math.ceil(shown.length / PER_PAGE));
  const pageItems = shown.slice((page - 1) * PER_PAGE, page * PER_PAGE);
  const groups = groupByDay(pageItems, 'created_at');

  return (
    <div className="w-full">
      <div className="mb-5">
        <h1 className="font-display text-2xl font-bold text-fg flex items-center gap-2">
          <MessageSquare className="w-6 h-6 text-orange" /> Feedback
        </h1>
        <p className="text-fg-muted text-sm mt-1">
          What members of {active?.name || 'your organization'} have shared.
        </p>
      </div>

      {/* category filter */}
      <div className="flex flex-wrap justify-end gap-3 mb-5">
        <select value={filterCat} onChange={(e) => { setFilterCat(e.target.value); setPage(1); }}
          className="px-3 py-2.5 bg-field border border-field-border rounded-lg text-fg text-sm">
          <option value="">All categories</option>
          <option value="idea">Idea</option>
          <option value="bug">Bug</option>
          <option value="praise">Praise</option>
          <option value="other">Other</option>
        </select>
      </div>

      {msg && (
        <div className="mb-4 p-3 rounded-lg border text-sm bg-red-500/10 border-red-500/25 text-red-400">
          {msg.text}
        </div>
      )}

      {loading ? (
        <div className="space-y-2">
          <SkeletonCard /><SkeletonCard /><SkeletonCard />
        </div>
      ) : shown.length === 0 ? (
        <div className="bg-panel border border-panel-border rounded-2xl p-12 text-center">
          <div className="inline-flex items-center justify-center h-14 w-14 rounded-2xl bg-orange/10 text-orange mb-4">
            <MessageSquare className="w-7 h-7" />
          </div>
          <div className="text-fg font-medium">{filterCat ? 'No feedback in this category' : 'No feedback yet'}</div>
          <p className="text-fg-muted text-sm mt-1 max-w-sm mx-auto">
            {filterCat ? 'Try a different category.' : "When a member shares what's working or what to improve, it'll appear here."}
          </p>
        </div>
      ) : (
        <>
        <div className="space-y-6">
          {groups.map((g) => (
            <div key={g.key}>
              <div className="flex items-center gap-3 mb-2">
                <span className="text-xs font-semibold uppercase tracking-wide text-fg-muted">{g.label}</span>
                <span className="h-px flex-1 bg-panel-border" />
                <span className="text-xs text-fg-muted tabular-nums">{g.items.length}</span>
              </div>
              <div className="space-y-2">
                {g.items.map((f) => (
                  <div key={f.request_id} onClick={() => setSelected(f)}
                    className="bg-panel border border-panel-border rounded-xl p-4 transition-colors hover:border-fg/15 cursor-pointer">
                    <div className="flex items-start gap-3">
                      <Avatar name={f.name} email={f.email} />
                      <div className="min-w-0 flex-1">
                        <div className="flex items-start justify-between gap-3">
                          <div className="min-w-0 flex items-center gap-2 flex-wrap">
                            <span className="text-sm text-fg font-medium truncate">{f.subject || 'Feedback'}</span>
                            <CategoryTag category={f.category} />
                          </div>
                          {f.created_at && (
                            <span className="text-xs text-fg-muted shrink-0 tabular-nums">{timeOf(f.created_at)}</span>
                          )}
                        </div>
                        <div className="mt-3">
                          <div className="text-sm text-fg truncate">{f.email || f.name || f.user_id}</div>
                          {f.email && f.name && <div className="text-xs text-fg-muted truncate">{f.name}</div>}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
        {totalPages > 1 && (
          <div className="flex items-center justify-between mt-6">
            <button onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page <= 1}
              className="flex items-center gap-1 px-3 py-1.5 bg-field border border-field-border text-fg rounded-lg text-sm hover:border-orange disabled:opacity-40 disabled:hover:border-field-border transition-colors">
              <ChevronLeft className="w-4 h-4" /> Back
            </button>
            <span className="text-xs text-fg-muted tabular-nums">Page {page} of {totalPages}</span>
            <button onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page >= totalPages}
              className="flex items-center gap-1 px-3 py-1.5 bg-field border border-field-border text-fg rounded-lg text-sm hover:border-orange disabled:opacity-40 disabled:hover:border-field-border transition-colors">
              Next <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        )}
        </>
      )}

      {/* feedback detail modal */}
      {selected && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
          onClick={() => setSelected(null)}>
          <div className="w-full max-w-2xl min-h-[360px] bg-panel border border-panel-border rounded-2xl p-6 shadow-2xl shadow-black/40"
            onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
            {/* modal title */}
            <div className="flex items-center justify-between gap-3 mb-5">
              <h2 className="font-display text-2xl font-bold text-fg flex items-center gap-2">
                <MessageSquare className="w-6 h-6 text-orange" /> Feedback
              </h2>
              <button onClick={() => setSelected(null)} className="text-fg-muted hover:text-fg shrink-0"><X className="w-5 h-5" /></button>
            </div>

            <div className="space-y-5">
              {/* From */}
              <div>
                <h3 className="font-display text-base font-bold text-fg">From</h3>
                <div className="mt-2 flex items-center justify-between gap-3 text-sm text-fg-muted">
                  <span className="truncate">{selected.email || selected.name || selected.user_id}</span>
                  {selected.created_at && (
                    <span className="shrink-0 tabular-nums">
                      {timeOf(selected.created_at)}, {new Date(selected.created_at).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}
                    </span>
                  )}
                </div>
              </div>

              {/* Title */}
              <div>
                <h3 className="font-display text-base font-bold text-fg">Title</h3>
                <p className="mt-2 text-sm text-fg-muted break-words">{selected.subject || 'Feedback'}</p>
              </div>

              {/* Category — only when present */}
              {selected.category && (
                <div>
                  <h3 className="font-display text-base font-bold text-fg">Category</h3>
                  <div className="mt-2"><CategoryTag category={selected.category} /></div>
                </div>
              )}

              {/* Message */}
              <div>
                <h3 className="font-display text-base font-bold text-fg">Message</h3>
                <p className="mt-2 text-sm text-fg-muted whitespace-pre-wrap break-words">{selected.message}</p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
